import React, { Fragment } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "store";
import { useAppDispatch } from "store/hooks";
import { toggleTheme } from "store/themeSlice";
import { logout } from "store/Auth/thunks";
import { selectHasWallets } from "store/WalletsStore";
import { selectIsNetworkOperationPending } from "store/networkOperationSlice";
import { ASIAccountSwitcher } from "components/ASIAccountSwitcher";
import { DeleteWalletModal } from "components/DeleteWalletModal";
import { useDeleteActiveWallet } from "hooks";
import { Button } from "components/Button";
import {
    SunIcon,
    MoonIcon,
    MenuIcon,
    LogoutIcon,
    DeleteIcon,
} from "components/Icons";

const HeaderStyled = styled.header`
    background: ${({ theme }) => theme.card};
    border-bottom: 1px solid ${({ theme }) => theme.border};
    height: ${({ theme }) => theme.layout.headerHeight};
    padding: 3px ${({ theme }) => theme.layout.gutterMobile};
    position: sticky;
    top: 0;
    z-index: ${({ theme }) => theme.zIndices.header};

    @media (min-width: calc(${({ theme }) => theme.breakpoints.mobile} + 1px)) {
        padding: 3px ${({ theme }) => theme.layout.gutterDesktop};
    }
`;

const HeaderTop = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
`;

const LeftSection = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
    min-width: 0;
    overflow: hidden;
`;

const LogoContainer = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px;
    border: 0;
    background: transparent;
    color: inherit;
    cursor: pointer;

    &:focus-visible {
        outline: none;
        border-radius: ${({ theme }) => theme.radii.sm};
        box-shadow: inset 0 0 0 2px ${({ theme }) => theme.focusRing};
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.4;
    }
`;

const LogoText = styled.h1`
    font-family: ${({ theme }) => theme.typography.fontFamily};
    font-size: ${({ theme }) => theme.typography.size.lg};
    font-weight: ${({ theme }) => theme.typography.weight.bold};
    line-height: 27px;
    color: ${({ theme }) => theme.text.primary};
    margin: 0;
    display: none;

    @media (min-width: 1024px) {
        display: block;
    }
`;

const HeaderActions = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;

    @media (min-width: 769px) {
        gap: 16px;
    }
`;

const IconButton = styled(Button).attrs({
    variant: "icon-button-black",
    size: "large",
})``;

const AsideMenuToggle = styled(IconButton)`
    @media (min-width: calc(${({ theme }) => theme.breakpoints.navigation} + 1px)) {
        display: none;
    }
`;

const DesktopButton = styled(IconButton)`
    @media (max-width: ${({ theme }) => theme.breakpoints.laptop}) {
        display: none;
    }
`;

interface LogoImageProps {
    isDarkMode: boolean;
}

const LogoImage = ({ isDarkMode }: LogoImageProps) => {
    const fillColor = isDarkMode ? "#FFFFFF" : "#000000";

    return (
        <svg
            width="34"
            height="23"
            viewBox="0 0 186 126"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M0.473633 63.0687C0.473633 51.6685 9.70995 42.4211 21.1101 42.4211C32.5103 42.4211 41.7596 33.1829 41.7596 21.7827C41.7596 10.3844 51.0071 1.13504 62.4073 1.13504C73.8075 1.13504 83.0438 10.3844 83.0438 21.7827C83.0438 33.1829 73.8075 42.4211 62.4073 42.4211C51.0071 42.4211 41.7596 51.6685 41.7596 63.0687C41.7596 74.4689 51.0071 83.7052 62.4073 83.7052C73.8075 83.7052 83.0438 92.9545 83.0438 104.355C83.0438 115.753 73.8075 124.991 62.4073 124.991C51.0071 124.991 41.7596 115.753 41.7596 104.355C41.7596 92.9545 32.5103 83.7052 21.1101 83.7052C9.70995 83.7052 0.473633 74.4689 0.473633 63.0687Z"
                fill={fillColor}
                fillOpacity="0.87"
            />
            <path
                d="M185.525 63.0576C185.525 74.4577 176.289 83.7052 164.889 83.7052C153.489 83.7052 144.241 92.9434 144.241 104.344C144.241 115.742 134.992 124.991 123.592 124.991C112.191 124.991 102.955 115.742 102.955 104.344C102.955 92.9434 112.191 83.7052 123.592 83.7052C134.992 83.7052 144.241 74.4577 144.241 63.0576C144.241 51.6574 134.992 42.4211 123.592 42.4211C112.191 42.4211 102.955 33.1717 102.955 21.7716C102.955 10.3732 112.191 1.13504 123.592 1.13504C134.992 1.13504 144.241 10.3732 144.241 21.7716C144.241 33.1717 153.489 42.4211 164.889 42.4211C176.289 42.4211 185.525 51.6574 185.525 63.0576Z"
                fill={fillColor}
                fillOpacity="0.87"
            />
            <path
                d="M93.1989 82.8094C104.278 82.8094 113.26 73.8274 113.26  62.7482C113.26 51.6672 104.278 42.6852 93.1989 42.6852C82.1179 42.6852 73.1377 51.6672 73.1377 62.7482C73.1377 73.8274 82.1179 82.8094 93.1989 82.8094Z"
                fill={fillColor}
                fillOpacity="0.87"
            />
        </svg>
    );
};

interface HeaderBarProps {
    onMobileMenuToggle: () => void;
    isMobileMenuOpen?: boolean;
}

export const HeaderBar: React.FC<HeaderBarProps> = ({
    onMobileMenuToggle,
    isMobileMenuOpen = false,
}) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { darkMode } = useSelector((state: RootState) => state.theme);
    const hasWallets = useSelector(selectHasWallets);
    const isAuthenticated = useSelector(
        (state: RootState) => state.auth.isAuthenticated,
    );
    const isNetworkOperationPending = useSelector(
        selectIsNetworkOperationPending,
    );

    const deleteWallet = useDeleteActiveWallet();

    const handleThemeToggle = () => {
        dispatch(toggleTheme());
    };

    const handleLogout = () => {
        if (isNetworkOperationPending) return;

        dispatch(logout());
        navigate("/login");
    };

    return (
        <Fragment>
            <HeaderStyled>
                <HeaderTop>
                    <LeftSection>
                        <LogoContainer
                            type="button"
                            aria-label="Go to wallet home"
                            onClick={() => navigate("/")}
                            disabled={isNetworkOperationPending}
                        >
                            <LogoImage isDarkMode={darkMode} />
                            <LogoText>ASI:Chain Wallet</LogoText>
                        </LogoContainer>
                    </LeftSection>

                    <HeaderActions>
                        {isAuthenticated && hasWallets && (
                            <ASIAccountSwitcher />
                        )}
                        <IconButton
                            onClick={handleThemeToggle}
                            aria-label={
                                darkMode
                                    ? "Switch to Light Mode"
                                    : "Switch to Dark Mode"
                            }
                            title={
                                darkMode
                                    ? "Switch to Light Mode"
                                    : "Switch to Dark Mode"
                            }
                        >
                            {darkMode ? (
                                <SunIcon size={20} />
                            ) : (
                                <MoonIcon size={20} />
                            )}
                        </IconButton>

                        {isAuthenticated && (
                            <DesktopButton
                                onClick={handleLogout}
                                title={"Logout"}
                                aria-label="Logout"
                                disabled={isNetworkOperationPending}
                            >
                                <LogoutIcon size={20} />
                            </DesktopButton>
                        )}

                        {isAuthenticated && hasWallets && (
                            <DesktopButton
                                id="delete-wallet-button"
                                onClick={deleteWallet.open}
                                title={"Delete Wallet"}
                                aria-label="Delete Wallet"
                                disabled={isNetworkOperationPending}
                            >
                                <DeleteIcon size={20} />
                            </DesktopButton>
                        )}

                        <AsideMenuToggle
                            id="sidebar-menu-button"
                            onClick={onMobileMenuToggle}
                            title="Open navigation"
                            aria-label="Open navigation"
                            aria-expanded={isMobileMenuOpen}
                            aria-controls="mobile-nav-drawer"
                        >
                            <MenuIcon size={20} />
                        </AsideMenuToggle>
                    </HeaderActions>
                </HeaderTop>
            </HeaderStyled>
            <DeleteWalletModal
                isOpen={deleteWallet.isOpen}
                isDeleting={deleteWallet.isDeleting}
                error={deleteWallet.error}
                onConfirm={deleteWallet.confirm}
                onCancel={deleteWallet.close}
            />
        </Fragment>
    );
};
